import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FiEdit3, FiSearch } from "react-icons/fi";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import GuideSection from "../components/guide/GuideSection";
import { businessNameGuide } from "../components/guide/guideContent";
import {
  CREATION_STEPS,
  SummaryBar,
  showError,
} from "../components/options/OptionParts";
import { addToCart } from "../redux/actions/CartActions";
import {
  addToServiceList,
  removeFromServiceList,
} from "../redux/actions/ServiceListActions";
import { updateBusinessRequest } from "../redux/actions/RequestActions";
import pricing from "../assets/data/pricing.json";
import serviceCode from "../assets/data/serviceCode.json";
import { formatPrice } from "../utils/formatPrice";
import { CLASSIC, isAssociation, procedureFor } from "../utils/creationFlow";
import "./styles/options.css";
import "./styles/flow-form.css";

const HAVE = "have";
const FIND = "find";
const MIN_NAME_LENGTH = 2;

const emailSchema = Yup.string().trim().email();

function BusinessName() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { services } = useSelector((state) => state.serviceList);
  const { businessCreationRequest } = useSelector((state) => state.request);

  const association = isAssociation(cart.businessType);
  const nounLabel = association ? "votre association" : "votre entreprise";

  // Previous answers are restored when the user comes back to this step
  const [choice, setChoice] = useState(() =>
    association || cart.haveBusinessName === true
      ? HAVE
      : cart.haveBusinessName === false
        ? FIND
        : null,
  );
  const [name, setName] = useState(
    typeof cart.businessName === "string" ? cart.businessName : "",
  );
  const [phone, setPhone] = useState(cart.companyPhone || "");
  const [email, setEmail] = useState(cart.companyEmail || "");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const nameError =
    choice === HAVE && name.trim().length < MIN_NAME_LENGTH
      ? "Veuillez saisir le nom souhaité."
      : null;
  const emailError =
    email.trim() && !emailSchema.isValidSync(email)
      ? "L'adresse e-mail est invalide."
      : null;
  const isValid = Boolean(choice) && !nameError && !emailError;

  const nameCode = serviceCode.businessName;
  const withNameSearch = choice === FIND;

  const handleContinue = async () => {
    setTouched(true);
    if (!isValid) return;

    setSubmitting(true);

    const updatedServices = [
      ...services.filter((service) => service.code !== nameCode),
      ...(withNameSearch ? [{ code: nameCode, billing: "lifetime" }] : []),
    ];

    const payload = {
      managerType: cart.managerType,
      managerName:
        cart.managerType === "myself"
          ? `${user.firstname} ${user.lastname}`
          : cart.managerName,
      procedure: association
        ? CLASSIC
        : procedureFor(cart.businessType, cart.procedure),
      businessName: withNameSearch ? "" : name.trim(),
      companyPhone: phone,
      companyEmail: email.trim(),
      services: updatedServices,
    };

    if (!association) payload.activitySector = cart.sectorActivity;

    await dispatch(
      updateBusinessRequest(
        user.token,
        businessCreationRequest?.reference,
        payload,
        handleSaved,
      ),
    );
  };

  const handleSaved = async ({ success, message }) => {
    if (!success) {
      setSubmitting(false);
      showError(Swal, message);
      return;
    }

    await dispatch(
      addToCart({
        haveBusinessName: !withNameSearch,
        businessName: withNameSearch
          ? { amount: pricing.businessName }
          : name.trim(),
        companyPhone: phone,
        companyEmail: email.trim(),
      }),
    );

    if (withNameSearch) {
      await dispatch(addToServiceList({ code: nameCode, billing: "lifetime" }));
    } else {
      await dispatch(removeFromServiceList(nameCode));
    }

    setSubmitting(false);
    navigate("/documents");
  };

  const label = !choice
    ? "Choisissez une option pour continuer"
    : withNameSearch
      ? "Recherche du nom par Platform98"
      : name.trim()
        ? `Nom : ${name.trim()}`
        : "Saisissez le nom pour continuer";

  return (
    <div className="options-page flow-fields">
      <Header />
      <ProgressBar steps={CREATION_STEPS} currentStep="Entreprise" />

      <div className="container options-container">
        <header className="options-header">
          <h1 className="options-title">
            Le <span>nom</span> de {nounLabel}
          </h1>
          <p className="options-lead">
            {association
              ? "Indiquez le nom de votre association, puis ses coordonnées si vous le souhaitez."
              : "Indiquez le nom de votre choix ou laissez-nous le trouver avec vous."}
          </p>
        </header>

        {!association && (
          <div
            className="seat-choices"
            role="radiogroup"
            aria-label="Nom de l'entreprise"
          >
            <button
              type="button"
              role="radio"
              aria-checked={choice === HAVE}
              className={`seat-choice ${choice === HAVE ? "is-selected" : ""}`}
              onClick={() => setChoice(HAVE)}
            >
              <span className="option-icon" aria-hidden="true">
                <FiEdit3 />
              </span>
              <span className="seat-choice-text">
                <strong>J'ai déjà un nom</strong>
                <span>Vous indiquez le nom de votre entreprise.</span>
              </span>
              <span className="seat-radio" aria-hidden="true" />
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={withNameSearch}
              className={`seat-choice ${withNameSearch ? "is-selected" : ""}`}
              onClick={() => setChoice(FIND)}
            >
              <span className="option-icon" aria-hidden="true">
                <FiSearch />
              </span>
              <span className="seat-choice-text">
                <strong>Trouvez-le pour moi</strong>
                <span>
                  Nous vous proposons un nom disponible ·{" "}
                  <b className="name-price">
                    {formatPrice(pricing.businessName)}
                  </b>
                </span>
              </span>
              <span className="seat-radio" aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="seat-panel name-panel">
          {choice === HAVE && (
            <div className="flow-field">
              <label htmlFor="business-name" className="flow-label">
                Nom de {nounLabel}
              </label>
              <input
                id="business-name"
                type="text"
                className={`flow-input ${
                  touched && nameError ? "is-invalid" : ""
                }`}
                placeholder={
                  association
                    ? "Ex. : Les Amis du Wouri"
                    : "Ex. : Kribi Digital"
                }
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="organization"
              />
              {touched && nameError && (
                <p className="flow-error">{nameError}</p>
              )}
            </div>
          )}

          <p className="name-panel-title">
            Coordonnées de {nounLabel} <small>(facultatif)</small>
          </p>

          <div className="flow-form-row">
            <div className="flow-field">
              <label htmlFor="company-phone" className="flow-label">
                Téléphone
              </label>
              <PhoneInput
                country="cm"
                value={phone}
                onChange={(value) =>
                  // Only the country code means no number was typed
                  setPhone(value && value.length > 5 ? `+${value}` : "")
                }
                inputProps={{ id: "company-phone" }}
                inputClass="flow-phone"
                containerClass="flow-phone-container"
                placeholder="Numéro de téléphone"
              />
            </div>

            <div className="flow-field">
              <label htmlFor="company-email" className="flow-label">
                E-mail
              </label>
              <input
                id="company-email"
                type="email"
                className={`flow-input ${emailError ? "is-invalid" : ""}`}
                placeholder="contact@exemple.cm"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
              />
              {emailError && <p className="flow-error">{emailError}</p>}
            </div>
          </div>
        </div>
      </div>

      {!association && <GuideSection {...businessNameGuide} />}

      <SummaryBar
        label={label}
        total={withNameSearch ? `+ ${formatPrice(pricing.businessName)}` : "—"}
        buttonLabel={submitting ? "Enregistrement…" : "Continuer"}
        disabled={submitting || !choice}
        onContinue={handleContinue}
      />
    </div>
  );
}

export default BusinessName;
